/**
 * GTM purchase bridge: writes checkout_data + customer_info before thank-you redirect.
 * Used by cart checkout and popup checkout on successful create_order.php response.
 *
 * customer_info (for Meta / GTM): name, phone (E.164 212…), city, address, email, country (ma)
 */
(function (global) {
  'use strict';

  var CURRENCY = 'MAD';
  var CATEGORY = 'Clothing';
  var COUNTRY = 'ma';

  function storageSet(key, value) {
    try {
      global.localStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Morocco mobile → Meta E.164 (digits only, no +): 212XXXXXXXXX (12 digits).
   * Handles: 0612…, 612…, 212612…, +212612…, +212 612…, 00212…, 2120612… (typo)
   */
  function normalizeMaPhone(phone) {
    var digits = String(phone || '').replace(/\D/g, '');
    if (!digits) return '';

    // 00212… → 212…
    if (digits.indexOf('00212') === 0) {
      digits = digits.slice(2);
    }

    function isMaMobileE164(d) {
      return /^212[567]\d{8}$/.test(d);
    }

    if (digits.indexOf('212') === 0) {
      var rest = digits.slice(3);
      // 2120XXXXXXXX → user kept the trunk 0 after country code
      if (rest.charAt(0) === '0' && rest.length >= 10) {
        rest = rest.slice(1);
      }
      digits = '212' + rest;
      if (digits.length > 12) {
        digits = digits.slice(0, 12);
      }
      if (isMaMobileE164(digits)) return digits;
    }

    // 0XXXXXXXXX (10-digit local)
    if (digits.charAt(0) === '0' && digits.length >= 10) {
      var national = digits.slice(1, 10);
      if (/^[567]\d{8}$/.test(national)) {
        return '212' + national;
      }
    }

    // 9-digit national (612345678)
    if (digits.length === 9 && /^[567]\d{8}$/.test(digits)) {
      return '212' + digits;
    }

    return isMaMobileE164(digits) ? digits : '';
  }

  /** Same pattern as Shopify order API: {e164}@bambe.ma */
  function syntheticEmail(phone) {
    var e164 = normalizeMaPhone(phone);
    if (!e164) return '';
    return (e164 + '@bambe.ma').toLowerCase();
  }

  /**
   * Meta fn/ln normalization: trim, lowercase, drop punctuation, collapse spaces.
   * Keeps Latin, accented and Arabic letters; only punctuation is stripped.
   */
  function normalizeNamePart(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[._\-–—,;:!?'"“”‘’`^~()\[\]{}<>/\\|@#$%&*+=]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Meta ct: lowercase, no punctuation, no spaces (Arabic UTF-8 kept).
   * e.g. "Casablanca" → "casablanca", "El Jadida" → "eljadida"
   */
  function normalizeCity(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[._\-–—,;:!?'"“”‘’`^~()\[\]{}<>/\\|@#$%&*+=]/g, '')
      .replace(/\s+/g, '');
  }

  /**
   * One "full name" field → { first_name, last_name }.
   * First word = first name, everything after = last name.
   * Single word → first name only, last name stays empty (never invent data).
   */
  function splitFullName(fullName) {
    var cleaned = String(fullName || '').trim().replace(/\s+/g, ' ');
    if (!cleaned) return { first_name: '', last_name: '' };
    var tokens = cleaned.split(' ');
    var first = tokens.shift();
    var last = tokens.join(' ');
    return {
      first_name: normalizeNamePart(first),
      last_name: normalizeNamePart(last)
    };
  }

  function normalizeCustomer(customer) {
    customer = customer || {};
    var rawPhone = String(customer.phone || '').trim();
    var e164 = normalizeMaPhone(rawPhone);
    var fullName = String(customer.name || '').trim();
    var parts = splitFullName(fullName);
    return {
      name: fullName,
      first_name: parts.first_name,
      last_name: parts.last_name,
      phone: e164,
      city: normalizeCity(customer.city),
      address: String(customer.address || '').trim(),
      email: syntheticEmail(rawPhone),
      country: COUNTRY
    };
  }

  function buildUserData(customer) {
    var c = normalizeCustomer(customer);
    return {
      name: c.name,
      first_name: c.first_name,
      last_name: c.last_name,
      phone: c.phone,
      city: c.city,
      address: c.address,
      email: c.email,
      country: c.country
    };
  }

  /** create_order.php returns { status, data: { order_id, order_internal_id, ... } } */
  function extractOrderId(resp) {
    if (!resp) return null;
    var d = resp.data && typeof resp.data === 'object' ? resp.data : resp;
    var id =
      resp.order_id ||
      resp.orderId ||
      resp.id ||
      d.order_id ||
      d.order_internal_id ||
      d.shopify_completed_order_id ||
      d.shopify_order_id ||
      null;
    if (id == null || id === '') return null;
    return String(id);
  }

  function normalizeItems(items) {
    if (!items || !items.length) return [];
    return items.map(function (item) {
      return {
        item_id: String(item.item_id != null ? item.item_id : ''),
        item_name: String(item.item_name || 'منتج'),
        item_category: item.item_category || CATEGORY,
        price: Number(item.price) || 0,
        quantity: Number(item.quantity) || 1
      };
    });
  }

  function computeTotal(subtotal, shippingFees, coupon) {
    var finalPrice = Number(subtotal) || 0;
    if (coupon && coupon.discount_percent) {
      finalPrice -= Math.floor((finalPrice * coupon.discount_percent) / 100);
    }
    return Math.round(finalPrice + (Number(shippingFees) || 0));
  }

  function saveCustomerInfo(customer) {
    return storageSet('customer_info', JSON.stringify(normalizeCustomer(customer)));
  }

  function saveCheckoutData(payload) {
    var checkout = {
      currency: payload.currency || CURRENCY,
      value: Number(payload.value) || 0,
      items: normalizeItems(payload.items)
    };
    var ok = storageSet('checkout_data', JSON.stringify(checkout));
    if (payload.orderId != null && payload.orderId !== '') {
      storageSet('bambe_transaction_id', String(payload.orderId));
    }
    return ok;
  }

  /**
   * Call on order API success, before redirect to thank-you.
   */
  function saveForPurchase(opts) {
    opts = opts || {};
    saveCustomerInfo(opts.customer);
    return saveCheckoutData(opts);
  }

  /**
   * Popup: set customer_info from pc-* fields before GTM lead click handler runs.
   */
  function preparePopupLead() {
    var nameEl = global.document.getElementById('pc-name');
    var phoneEl = global.document.getElementById('pc-phone');
    var cityEl = global.document.getElementById('pc-city');
    var addressEl = global.document.getElementById('pc-address');
    return saveCustomerInfo({
      name: nameEl ? nameEl.value : '',
      phone: phoneEl ? phoneEl.value : '',
      city: cityEl ? cityEl.value : '',
      address: addressEl ? addressEl.value : ''
    });
  }

  function fromCartSuccess(cartItems, subtotal, shippingFees, coupon, customer, orderId) {
    var items = (cartItems || []).map(function (item) {
      var unit = Math.round(
        (item.final_price != null ? item.final_price : item.price) / 100
      );
      return {
        item_id: item.variant_id,
        item_name: item.product_title || 'منتج',
        item_category: CATEGORY,
        price: unit,
        quantity: item.quantity
      };
    });
    return saveForPurchase({
      currency: CURRENCY,
      value: computeTotal(subtotal, shippingFees, coupon),
      items: items,
      customer: customer,
      orderId: orderId
    });
  }

  function fromPopupSuccess(nums, variantId, productTitle, customer, orderId) {
    var qty = nums.billingQty != null ? nums.billingQty : nums.qty;
    var unitPrice = Math.round(
      nums.unitPrice != null ? nums.unitPrice : nums.price / (qty || 1)
    );
    return saveForPurchase({
      currency: CURRENCY,
      value: nums.total != null ? nums.total : computeTotal(nums.price, nums.shipping, null),
      items: [
        {
          item_id: variantId,
          item_name: productTitle,
          item_category: CATEGORY,
          price: unitPrice,
          quantity: qty
        }
      ],
      customer: customer,
      orderId: orderId
    });
  }

  global.BambeGtmBridge = {
    COUNTRY: COUNTRY,
    normalizeMaPhone: normalizeMaPhone,
    syntheticEmail: syntheticEmail,
    normalizeNamePart: normalizeNamePart,
    normalizeCity: normalizeCity,
    splitFullName: splitFullName,
    normalizeCustomer: normalizeCustomer,
    buildUserData: buildUserData,
    extractOrderId: extractOrderId,
    saveForPurchase: saveForPurchase,
    preparePopupLead: preparePopupLead,
    fromCartSuccess: fromCartSuccess,
    fromPopupSuccess: fromPopupSuccess,
    computeTotal: computeTotal
  };
})(typeof window !== 'undefined' ? window : this);
